<template>
  <AppShell
    active="sources"
    :workspace="workspace"
    title="Source Intelligence"
    subtitle="Freshness, health, coverage, and refresh guidance for every source Landport uses."
    max-width-class="max-w-none"
    data-testid="source-intelligence-page"
  >
    <template #actions>
      <a href="/map" class="hidden h-10 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted sm:inline-flex">
        <MapPinned class="size-4" aria-hidden="true" />
        Map
      </a>
      <button
        type="button"
        class="inline-flex h-10 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-wait disabled:opacity-70"
        :disabled="syncing"
        @click="syncCatalogs"
      >
        <LoaderCircle v-if="syncing" class="size-4 animate-spin" aria-hidden="true" />
        <RefreshCw v-else class="size-4" aria-hidden="true" />
        Sync catalogs
      </button>
    </template>

    <div v-if="notice" class="mb-4 rounded-md border border-land-border bg-land-primary-soft px-4 py-3 text-sm font-medium text-land-primary" role="status">{{ notice }}</div>
    <div v-if="errors.length > 0" class="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
      <p v-for="error in errors" :key="error">{{ error }}</p>
    </div>

    <section class="rounded-md border border-land-border bg-land-surface shadow-sm" aria-label="Source health metrics">
      <div class="source-metric-grid">
        <article v-for="metric in metricCards" :key="metric.key" class="min-w-0 px-4 py-3">
          <p class="truncate text-xs font-medium text-land-ink-soft">{{ metric.label }}</p>
          <div class="mt-1 flex items-baseline gap-2">
            <p class="text-xl font-semibold tracking-normal">{{ metric.value }}</p>
            <p class="truncate text-xs text-land-ink-muted">{{ metric.detail }}</p>
          </div>
        </article>
      </div>
    </section>

    <section v-if="attentionSources.length > 0" class="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p class="text-sm font-semibold text-amber-950">Sources needing attention</p>
          <p class="mt-1 text-sm leading-6 text-amber-800">These sources are missing, stale, limited, or require an explicit manual/external refresh.</p>
        </div>
        <StateBadge :label="`${attentionSources.length} flagged`" tone="amber" />
      </div>
      <div class="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        <button
          v-for="source in attentionSources.slice(0, 6)"
          :key="`attention-${source.key}`"
          type="button"
          class="rounded-md border border-amber-200 bg-white px-3 py-2 text-left hover:bg-amber-100/60"
          @click="openSource(source)"
        >
          <span class="flex items-center justify-between gap-2">
            <span class="truncate text-sm font-semibold text-land-ink">{{ source.name }}</span>
            <StateBadge :label="source.statusLabel" :tone="source.statusTone" />
          </span>
          <span class="mt-1 block truncate text-xs text-land-ink-muted">{{ source.refreshInstruction }}</span>
        </button>
      </div>
    </section>

    <section class="mt-4 rounded-md border border-land-border bg-land-surface shadow-sm">
      <div class="source-filter-grid border-b border-land-border-soft p-3">
        <label class="relative block">
          <span class="sr-only">Search sources</span>
          <Search class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-land-ink-soft" aria-hidden="true" />
          <input v-model="searchQuery" type="search" placeholder="Search source, provider, category, worker..." class="h-10 w-full rounded-md border border-land-border bg-white py-2 pl-9 pr-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
        </label>

        <label class="grid h-10 grid-cols-[70px_minmax(0,1fr)] items-center rounded-md border border-land-border bg-white">
          <span class="border-r border-land-border-soft px-3 text-xs font-semibold uppercase text-land-ink-soft">Group</span>
          <select v-model="selectedGroup" aria-label="Filter by source group" class="h-full min-w-0 rounded-r-md border-0 bg-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-land-primary/20">
            <option value="all">All</option>
            <option v-for="group in filters.groups" :key="group" :value="group">{{ titleize(group) }}</option>
          </select>
        </label>

        <label class="grid h-10 grid-cols-[70px_minmax(0,1fr)] items-center rounded-md border border-land-border bg-white">
          <span class="border-r border-land-border-soft px-3 text-xs font-semibold uppercase text-land-ink-soft">Status</span>
          <select v-model="selectedStatus" aria-label="Filter by source status" class="h-full min-w-0 rounded-r-md border-0 bg-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-land-primary/20">
            <option value="all">All</option>
            <option v-for="status in filters.statuses" :key="status" :value="status">{{ titleize(status) }}</option>
          </select>
        </label>

        <label class="grid h-10 grid-cols-[78px_minmax(0,1fr)] items-center rounded-md border border-land-border bg-white">
          <span class="border-r border-land-border-soft px-3 text-xs font-semibold uppercase text-land-ink-soft">Auto</span>
          <select v-model="selectedAutomation" aria-label="Filter by automation" class="h-full min-w-0 rounded-r-md border-0 bg-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-land-primary/20">
            <option value="all">All</option>
            <option v-for="automation in filters.automations" :key="automation" :value="automation">{{ titleize(automation) }}</option>
          </select>
        </label>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3 border-b border-land-border-soft px-4 py-2">
        <p class="text-sm font-semibold">{{ formatNumber(filteredSources.length) }} sources</p>
        <button v-if="hasActiveFilters" type="button" class="text-sm font-semibold text-land-primary hover:text-land-primary-hover" @click="clearFilters">
          Clear filters
        </button>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full min-w-[1120px] text-left text-sm">
          <thead class="bg-land-surface-muted text-xs font-semibold uppercase text-land-ink-soft">
            <tr>
              <th scope="col" class="px-4 py-3">Source</th>
              <th scope="col" class="px-4 py-3">Health</th>
              <th scope="col" class="px-4 py-3">Freshness</th>
              <th scope="col" class="px-4 py-3">Automation</th>
              <th scope="col" class="px-4 py-3">Coverage</th>
              <th scope="col" class="px-4 py-3 text-right">Open</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-land-border-soft">
            <tr
              v-for="source in visibleSources"
              :key="source.key"
              class="cursor-pointer bg-white hover:bg-land-surface-muted"
              :class="selectedSource?.key === source.key && detailOpen ? 'bg-land-primary-soft/60' : ''"
              @click="openSource(source)"
            >
              <td class="px-4 py-3">
                <div class="min-w-0">
                  <p class="truncate font-semibold text-land-ink">{{ source.name }}</p>
                  <p class="mt-1 truncate text-xs text-land-ink-soft">{{ source.kind }} · {{ source.provider }}</p>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-1.5">
                  <StateBadge :label="source.statusLabel" :tone="source.statusTone" />
                  <StateBadge :label="titleize(source.category)" tone="slate" />
                </div>
              </td>
              <td class="px-4 py-3">
                <p class="font-semibold text-land-ink">{{ source.count === null || source.count === undefined ? 'Live' : formatNumber(source.count) }}</p>
                <p class="mt-1 text-xs text-land-ink-soft">{{ source.lastUpdatedAt ? relativeDate(source.lastUpdatedAt) : source.cadence || 'No timestamp' }}</p>
              </td>
              <td class="px-4 py-3">
                <p class="font-semibold text-land-ink">{{ source.automationLabel }}</p>
                <p class="mt-1 text-xs text-land-ink-soft">{{ source.schedule?.cron || source.cadence || 'No schedule' }}</p>
              </td>
              <td class="px-4 py-3">
                <p class="max-w-[260px] truncate text-sm text-land-ink-muted">{{ source.coverage || source.caveat || 'Not specified' }}</p>
              </td>
              <td class="px-4 py-3 text-right">
                <button type="button" class="inline-flex size-8 items-center justify-center rounded-md border border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted" :aria-label="`Open ${source.name}`" @click.stop="openSource(source)">
                  <ArrowRight class="size-4" aria-hidden="true" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="filteredSources.length > visibleSources.length" class="border-t border-land-border-soft p-3 text-center">
        <button type="button" class="inline-flex h-9 items-center rounded-md border border-land-border bg-white px-3 text-sm font-semibold text-land-ink-muted hover:bg-land-surface-muted" @click="resultLimit += 100">
          Show more
        </button>
      </div>
    </section>

    <section class="mt-4 rounded-md border border-land-border bg-land-surface shadow-sm">
      <div class="border-b border-land-border-soft p-4">
        <h2 class="text-lg font-semibold tracking-normal">Scheduled Refreshes</h2>
        <p class="mt-1 text-sm text-land-ink-muted">Configured Sidekiq schedules. External Python sidecars still need their own cron/k8s schedule.</p>
      </div>
      <div class="grid gap-2 p-4 md:grid-cols-2 xl:grid-cols-3">
        <article v-for="schedule in schedules" :key="schedule.key" class="rounded-md border border-land-border-soft bg-white p-3">
          <p class="truncate text-sm font-semibold">{{ schedule.className }}</p>
          <p class="mt-1 font-mono text-xs text-land-ink-muted">{{ schedule.cron }}</p>
          <p class="mt-2 text-xs leading-5 text-land-ink-muted">{{ schedule.description }}</p>
        </article>
      </div>
    </section>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="detailOpen && selectedSource" class="fixed inset-0 z-50 bg-slate-950/35" role="presentation" @click.self="closeDetail">
        <aside class="ml-auto flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl" role="dialog" aria-modal="true" :aria-label="`${selectedSource.name} source details`">
          <header class="border-b border-land-border-soft bg-white p-5">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <div class="flex flex-wrap gap-1.5">
                  <StateBadge :label="selectedSource.statusLabel" :tone="selectedSource.statusTone" />
                  <StateBadge :label="selectedSource.automationLabel" tone="blue" />
                  <StateBadge :label="titleize(selectedSource.group)" tone="slate" />
                </div>
                <h2 class="mt-3 text-2xl font-semibold tracking-normal">{{ selectedSource.name }}</h2>
                <p class="mt-2 text-sm text-land-ink-muted">{{ selectedSource.provider }}</p>
              </div>
              <button type="button" class="grid size-9 shrink-0 place-items-center rounded-md border border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted" aria-label="Close source details" @click="closeDetail">
                <X class="size-4" aria-hidden="true" />
              </button>
            </div>

            <div class="mt-4 flex flex-wrap gap-2">
              <a v-if="selectedSource.sourceUrl" :href="selectedSource.sourceUrl" target="_blank" rel="noreferrer" class="inline-flex h-9 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted">
                <ExternalLink class="size-4" aria-hidden="true" />
                Source
              </a>
              <a href="/map" class="inline-flex h-9 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted">
                <MapPinned class="size-4" aria-hidden="true" />
                Map
              </a>
            </div>
          </header>

          <div class="min-h-0 flex-1 overflow-y-auto p-5">
            <section class="grid gap-4 md:grid-cols-2">
              <div>
                <h3 class="text-sm font-semibold uppercase text-land-ink-soft">Health</h3>
                <dl class="mt-3 grid gap-2">
                  <div v-for="fact in selectedFacts" :key="fact.label" class="grid grid-cols-[145px_minmax(0,1fr)] gap-3 rounded-md border border-land-border-soft bg-land-surface-muted px-3 py-2">
                    <dt class="text-xs font-medium text-land-ink-soft">{{ fact.label }}</dt>
                    <dd class="min-w-0 break-words text-sm font-semibold text-land-ink">{{ fact.value }}</dd>
                  </div>
                </dl>
              </div>

              <div>
                <h3 class="text-sm font-semibold uppercase text-land-ink-soft">Refresh Guidance</h3>
                <div class="mt-3 rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-sm leading-6 text-land-ink">{{ selectedSource.refreshInstruction }}</p>
                  <p v-if="selectedSource.schedule" class="mt-3 font-mono text-xs text-land-ink-muted">{{ selectedSource.schedule.cron }} · {{ selectedSource.schedule.queue || 'default' }}</p>
                </div>
                <div v-if="selectedSource.caveat" class="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p class="text-sm font-semibold text-amber-950">Caveat</p>
                  <p class="mt-1 text-sm leading-6 text-amber-800">{{ selectedSource.caveat }}</p>
                </div>
              </div>
            </section>

            <section class="mt-5">
              <h3 class="text-sm font-semibold uppercase text-land-ink-soft">Metadata</h3>
              <div class="mt-3 overflow-hidden rounded-md border border-land-border-soft">
                <table class="w-full text-left text-sm">
                  <tbody class="divide-y divide-land-border-soft">
                    <tr v-for="item in metadataRows" :key="item.key" class="bg-white">
                      <th class="w-48 px-3 py-2 text-xs font-semibold uppercase text-land-ink-soft">{{ titleize(item.key) }}</th>
                      <td class="px-3 py-2 text-land-ink-muted">{{ stringify(item.value) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </aside>
      </div>
    </Transition>
  </AppShell>
</template>

<script setup>
import {
  ArrowRight,
  ExternalLink,
  LoaderCircle,
  MapPinned,
  RefreshCw,
  Search,
  X
} from '@lucide/vue'
import { computed, ref } from 'vue'
import { normalizeErrors, requestJson } from '@/api/client'
import AppShell from '@/components/shared/app_shell.vue'
import StateBadge from '@/components/shared/state_badge.vue'

const props = defineProps({
  payload: {
    type: Object,
    required: true
  }
})

const dashboard = ref(props.payload)
const notice = ref('')
const errors = ref([])
const syncing = ref(false)
const searchQuery = ref('')
const selectedGroup = ref('all')
const selectedStatus = ref('all')
const selectedAutomation = ref('all')
const resultLimit = ref(120)
const selectedSource = ref(null)
const detailOpen = ref(false)

const workspace = computed(() => dashboard.value.workspace || {})
const summary = computed(() => dashboard.value.summary || {})
const filters = computed(() => dashboard.value.filters || { groups: [], statuses: [], automations: [] })
const sources = computed(() => dashboard.value.sources || [])
const schedules = computed(() => dashboard.value.schedules || [])

const metricCards = computed(() => [
  { key: 'total', label: 'Sources', value: formatNumber(summary.value.totalSources), detail: 'catalogued' },
  { key: 'healthy', label: 'Healthy / live', value: formatNumber(summary.value.healthy), detail: 'usable now' },
  { key: 'attention', label: 'Needs attention', value: formatNumber(summary.value.attention), detail: 'missing, stale, limited' },
  { key: 'automated', label: 'Automated', value: formatNumber(summary.value.automated), detail: 'scheduled/live/cache' },
  { key: 'manual', label: 'Manual / external', value: formatNumber(summary.value.manual), detail: 'requires action' },
  { key: 'planned', label: 'Planned', value: formatNumber(summary.value.planned), detail: 'coverage gaps' }
])

const attentionSources = computed(() => sources.value
  .filter((source) => ['not_loaded', 'stale', 'issue', 'limited'].includes(source.status) || ['external'].includes(source.automation))
  .sort((a, b) => Number(a.statusRank || 0) - Number(b.statusRank || 0)))

const filteredSources = computed(() => {
  const needle = searchQuery.value.trim().toLowerCase()

  return sources.value.filter((source) => {
    if (selectedGroup.value !== 'all' && source.group !== selectedGroup.value) return false
    if (selectedStatus.value !== 'all' && source.status !== selectedStatus.value) return false
    if (selectedAutomation.value !== 'all' && source.automation !== selectedAutomation.value) return false
    if (!needle) return true

    return [
      source.name,
      source.provider,
      source.category,
      source.kind,
      source.statusLabel,
      source.automationLabel,
      source.coverage,
      source.caveat,
      source.refreshInstruction,
      source.metadata?.scheduleClass,
      source.metadata?.model
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(needle))
  })
})

const visibleSources = computed(() => filteredSources.value.slice(0, resultLimit.value))
const hasActiveFilters = computed(() => searchQuery.value || selectedGroup.value !== 'all' || selectedStatus.value !== 'all' || selectedAutomation.value !== 'all')

const selectedFacts = computed(() => {
  const source = selectedSource.value
  if (!source) return []

  return [
    { label: 'Status', value: source.statusLabel },
    { label: 'Records', value: source.count === null || source.count === undefined ? 'Live / not cached' : formatNumber(source.count) },
    { label: 'Last updated', value: source.lastUpdatedAt ? formatDateTime(source.lastUpdatedAt) : 'No timestamp' },
    { label: 'Cadence', value: source.cadence || 'Not specified' },
    { label: 'Automation', value: source.automationLabel },
    { label: 'Coverage', value: source.coverage || 'Not specified' }
  ]
})

const metadataRows = computed(() => Object.entries(selectedSource.value?.metadata || {})
  .filter(([, value]) => value !== null && value !== undefined && value !== '')
  .map(([key, value]) => ({ key, value })))

const syncCatalogs = async () => {
  syncing.value = true
  notice.value = ''
  errors.value = []

  try {
    const data = await requestJson('/sources/sync_catalogs', { method: 'POST' })
    dashboard.value = data.sourceIntelligence
    notice.value = 'Source catalogs synced.'
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    syncing.value = false
  }
}

const openSource = (source) => {
  selectedSource.value = source
  detailOpen.value = true
}

const closeDetail = () => {
  detailOpen.value = false
}

const clearFilters = () => {
  searchQuery.value = ''
  selectedGroup.value = 'all'
  selectedStatus.value = 'all'
  selectedAutomation.value = 'all'
  resultLimit.value = 120
}

const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0))
const formatDateTime = (value) => new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
}).format(new Date(value))

const relativeDate = (value) => {
  if (!value) return 'No timestamp'

  const date = new Date(value)
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000)
  if (Number.isNaN(days)) return 'No timestamp'
  if (days <= 0) return 'Updated today'
  if (days === 1) return 'Updated yesterday'

  return `${days} days ago`
}

const stringify = (value) => {
  if (Array.isArray(value)) return value.join(', ')
  if (value && typeof value === 'object') return JSON.stringify(value)

  return String(value)
}

const titleize = (value) => String(value || '')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())
</script>

<style scoped>
.source-metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}

.source-metric-grid > article + article {
  border-left: 1px solid rgb(226 232 240);
}

.source-filter-grid {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(160px, 210px) minmax(160px, 210px) minmax(170px, 220px);
  gap: 0.75rem;
}

@media (max-width: 1024px) {
  .source-filter-grid {
    grid-template-columns: 1fr 1fr;
  }

  .source-metric-grid > article + article {
    border-left: 0;
    border-top: 1px solid rgb(226 232 240);
  }
}

@media (max-width: 640px) {
  .source-filter-grid {
    grid-template-columns: 1fr;
  }
}
</style>
